import React, { useRef } from 'react';
import { Instagram, Heart, MessageCircle, ArrowUpRight, ArrowLeft, ArrowRight } from 'lucide-react';
import { BRAND, INSTAGRAM_POSTS } from '../data/mock';
import { Reveal, AnimatedDivider, Magnetic } from './effects';

// ------------------------------------------------------------------
// INSTAGRAM POSTS — single horizontal scroll row (larger tiles)
// ------------------------------------------------------------------
const InstagramPosts = () => {
  const scrollRef = useRef(null);

  const scrollBy = (dir) => {
    if (!scrollRef.current) return;
    const card = scrollRef.current.querySelector('[data-tile="instagram"]');
    const step = card ? card.offsetWidth + 28 : 460;
    scrollRef.current.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <section
      className="relative bg-[#F5F3EF] pt-20 pb-10 lg:pt-28 lg:pb-12 overflow-hidden"
      aria-label="Instagram'da Tarhun Catering"
    >
      {/* Dot grid texture */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #1A1A1A 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="container-x relative">
        {/* SECTION HEADER */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-8 lg:mb-10">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-7">
              <AnimatedDivider width="3rem" />
              <Reveal delay={100} y={8} duration={700}>
                <span className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium">
                  Sosyal Vitrin · Tarhun Catering
                </span>
              </Reveal>
            </div>

            <Reveal delay={200}>
              <h2
                className="font-display text-[40px] lg:text-[60px] text-[#1A1A1A] leading-[1.04] tracking-[-0.02em]"
                style={{ fontWeight: 500 }}
              >
                Tarhun'un <span className="italic font-light text-[#72272D]">günlüğü</span>.
              </h2>
            </Reveal>

            <Reveal delay={400}>
              <p className="mt-7 max-w-xl text-[15.5px] lg:text-[16.5px] text-[#1A1A1A]/68 leading-[1.7]">
                Operasyondan kareler, ekibimizden hikâyeler. Instagram'da
                <strong className="font-medium text-[#1A1A1A]"> {BRAND.instagramHandle}</strong> hesabımızdan günlük olarak — şeffaf, gerçek, sahadan.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-5 flex flex-col lg:items-end gap-5">
            <Reveal delay={400}>
              <Magnetic strength={0.16}>
                <a
                  href={BRAND.instagram}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="instagram-follow-cta"
                  className="group inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F5F0E5] px-7 py-[14px] text-[11.5px] tracking-[0.24em] uppercase font-medium rounded-sm transition-colors duration-300 hover:bg-[#72272D]"
                >
                  <Instagram size={15} strokeWidth={1.6} />
                  Bizi Takip Edin
                  <ArrowUpRight
                    size={14}
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              </Magnetic>
            </Reveal>

            {/* Scroll arrows (desktop) */}
            <div className="hidden lg:flex items-center gap-3">
              <button
                type="button"
                onClick={() => scrollBy(-1)}
                aria-label="Önceki postlar"
                data-testid="instagram-scroll-prev"
                className="h-11 w-11 grid place-items-center border border-[#9A7F4F]/40 text-[#1A1A1A] rounded-sm hover:bg-[#1A1A1A] hover:text-[#F5F0E5] hover:border-[#1A1A1A] transition-colors"
              >
                <ArrowLeft size={15} strokeWidth={1.7} />
              </button>
              <button
                type="button"
                onClick={() => scrollBy(1)}
                aria-label="Sonraki postlar"
                data-testid="instagram-scroll-next"
                className="h-11 w-11 grid place-items-center border border-[#9A7F4F]/40 text-[#1A1A1A] rounded-sm hover:bg-[#1A1A1A] hover:text-[#F5F0E5] hover:border-[#1A1A1A] transition-colors"
              >
                <ArrowRight size={15} strokeWidth={1.7} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* HORIZONTAL SCROLL ROW — bleeds off the right edge */}
      <div
        ref={scrollRef}
        data-testid="instagram-scroll-row"
        className="flex gap-5 lg:gap-7 overflow-x-auto snap-x snap-mandatory scroll-smooth scrollbar-hide pl-6 sm:pl-10 lg:pl-20 pr-6 sm:pr-10 lg:pr-20"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {INSTAGRAM_POSTS.map((post, i) => (
          <Reveal key={post.id} delay={i * 70} y={20} duration={800}>
            <PostTile post={post} />
          </Reveal>
        ))}
      </div>

      {/* Hide WebKit scrollbar */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
      `}</style>
    </section>
  );
};

// ------------------------------------------------------------------
// SINGLE POST TILE — bigger square
// ------------------------------------------------------------------
const PostTile = ({ post }) => {
  return (
    <a
      href={post.href}
      target="_blank"
      rel="noreferrer"
      data-testid={`instagram-post-${post.id}`}
      data-tile="instagram"
      aria-label={`${post.caption} — Instagram'da gör`}
      className="group relative block flex-none w-[260px] sm:w-[320px] lg:w-[380px] aspect-[9/16] overflow-hidden rounded-sm bg-[#F5F3EF] snap-start shadow-[0_30px_70px_-30px_rgba(0,0,0,0.35)]"
    >
      <img
        src={post.image}
        alt={post.caption}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-contain transition-transform duration-[1100ms] ease-out group-hover:scale-[1.02]"
      />

      {/* Hover overlay — Instagram-style */}
      <div className="absolute inset-0 bg-[#0A0C0B]/74 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-between p-6 lg:p-8">
        {/* Top */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2.5 text-[#F5F0E5]/90">
            <Instagram size={17} strokeWidth={1.6} />
            <span className="text-[10.5px] tracking-[0.3em] uppercase font-medium">
              Post · {post.id}
            </span>
          </div>
          <ArrowUpRight
            size={20}
            strokeWidth={1.5}
            className="text-[#C9A875] transform translate-y-1 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500"
          />
        </div>

        {/* Bottom */}
        <div className="text-[#F5F0E5] transform translate-y-3 group-hover:translate-y-0 transition-transform duration-500">
          <p
            className="font-display text-[20px] lg:text-[24px] leading-[1.2] tracking-[-0.005em]"
            style={{ fontWeight: 500 }}
          >
            {post.caption}
          </p>
          <p className="mt-2.5 text-[13px] lg:text-[13.5px] text-[#F5F0E5]/70 leading-[1.55]">
            {post.excerpt}
          </p>

          <div className="mt-5 pt-4 border-t border-[#C9A875]/25 flex items-center gap-5 text-[10.5px] tracking-[0.18em] uppercase text-[#F5F0E5]/55 font-medium">
            <span className="flex items-center gap-1.5">
              <Heart size={13} strokeWidth={1.7} />
              {BRAND.instagramHandle}
            </span>
            <span className="flex items-center gap-1.5">
              <MessageCircle size={13} strokeWidth={1.7} />
              Yorumlar
            </span>
          </div>
        </div>
      </div>
    </a>
  );
};

export default InstagramPosts;
